import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Spinner, Alert, Form, Row, Col  } from 'react-bootstrap';
import axios from 'axios';
import ConsumableForm from './ConsumableForm';
import { Consumable, QueryParams } from '../interfaces'; // הוסף QueryParams
import MessageModal from './MessageModal'; // <--- חדש
import ConfirmationModal from './ConfirmationModal'; // <--- חדש
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faRotateLeft
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';


import { API_BASE_URL, buildApiUrl } from '../config/api';

const ConsumablesList: React.FC = () => {
  const { user } = useAuth();
  const [consumables, setConsumables] = useState<Consumable[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [consumableToEdit, setConsumableToEdit] = useState<Consumable | null>(null);

  // --- חדש: State לניהול המודאלים ---
  const [showMessageModal, setShowMessageModal] = useState<boolean>(false);
  const [messageModalTitle, setMessageModalTitle] = useState<string>('');
  const [messageModalContent, setMessageModalContent] = useState<string>('');
  const [messageModalVariant, setMessageModalVariant] = useState<'success' | 'danger' | 'info' | 'warning'>('info');

  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState<string>('');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmModalTitle, setConfirmModalTitle] = useState<string>('');

  // --- חדש: State לחיפוש, סינון ומיון ---
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterLowStock, setFilterLowStock] = useState<string>(''); // 'true', 'false', ''
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<string>('asc');

  // פונקציה לטעינת נתוני מתכלים עם פרמטרי שאילתה
  const fetchConsumables = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: QueryParams = {};
      if (searchQuery) params.search = searchQuery;
      // --- שינוי כאן: לוגיקת סינון מלאי נמוך ---
      if (filterLowStock === 'true') {
          params.low_stock = true; // שלח low_stock=true ל-Django
      } else if (filterLowStock === 'false') {
          params.low_stock = false; // שלח low_stock=false ל-Django
      }
      // --- סוף שינוי ---s
      if (sortBy) params.ordering = sortOrder === 'desc' ? `-${sortBy}` : sortBy;

      const response = await axios.get(buildApiUrl('settings/consumables/'), { params });
      setConsumables(response.data);
    } catch (err) {
      console.error("שגיאה בטעינת מתכלים:", err);
      setError("לא ניתן לטעון פריטים מתכלים. וודא ששרת Django פועל.");
    } finally {
      setLoading(false);
    }
  };

  // טען מתכלים בעת טעינת הרכיב או שינוי פרמטרים
  useEffect(() => {
    const handler = setTimeout(() => { // כדי להמתין שהקלדה תסתיים
      fetchConsumables();
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery, filterLowStock, sortBy, sortOrder]);

   // פונקציה להצגת הודעת מודאל
  const showMessage = (title: string, message: string, variant: 'success' | 'danger' | 'info' | 'warning' = 'info') => {
      setMessageModalTitle(title);
      setMessageModalContent(message);
      setMessageModalVariant(variant);
      setShowMessageModal(true);
    };

  const handleAddClick = () => { /* ... נשאר זהה ... */ setShowForm(true); };
  const handleEditClick = (item: Consumable) => { /* ... נשאר זהה ... */ setConsumableToEdit(item); setShowForm(true); };
  const handleDeleteClick = (id: number) => {
      setConfirmModalTitle('אישור מחיקה');
      setConfirmModalMessage('האם אתה בטוח שברצונך למחוק פריט מתכלה זה? פעולה זו בלתי הפיכה.');
      setConfirmAction(() => async () => {
        try {
          const item = consumables.find(c => c.id === id);
          await axios.delete(buildApiUrl(`settings/consumables/${id}/`));
          showMessage('הצלחה', `הפריט המתכלה ${item ? `"${item.name}" ` : ''}נמחק בהצלחה!`, 'success');
          fetchConsumables();
        } catch (err) {
          console.error("שגיאה במחיקת פריט מתכלה:", err);
          showMessage('שגיאה', 'שגיאה במחיקת הפריט המתכלה. אנא נסה שוב.', 'danger');
        }
      });
      setShowConfirmModal(true);
    };
  const handleResetToDefault = async (item: Consumable) => {
      if (!item.id) return;
      try {
        await axios.patch(buildApiUrl(`settings/consumables/${item.id}/`), {
          current_stock: item.default_quantity
        });
        showMessage('הצלחה', `המלאי של "${item.name}" אופס לכמות ברירת המחדל (${item.default_quantity}).`, 'success');
        fetchConsumables();
      } catch (err) {
        console.error("שגיאה באיפוס מתכלה לברירת מחדל:", err);
        showMessage('שגיאה', `לא ניתן לאפס את "${item.name}" לברירת המחדל כרגע.`, 'danger');
      }
    };
  const handleFormSuccess = () => {
      setShowForm(false);
      fetchConsumables();
      showMessage('הצלחה', consumableToEdit ? `פריט מתכלה "${consumableToEdit.name}" עודכן בהצלחה!` : 'פריט מתכלה חדש נוצר בהצלחה!', 'success');
    };
  const handleFormCancel = () => { setShowForm(false); setConsumableToEdit(null); };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSortIndicator = (field: string) => {
    if (sortBy === field) {
      return sortOrder === 'asc' ? ' 🔼' : ' 🔽';
    }
    return '';
  };


  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 app-sticky-page-toolbar">
        <h2>רשימת פריטים מתכלים</h2>
        <Button variant="success" onClick={handleAddClick}>
          <FontAwesomeIcon icon={faPlus} className="me-1" />
          הוסף פריט מתכלה חדש
        </Button>
      </div>

      {/* חדש: טופס חיפוש וסינון */}
      <Form className="mb-4 p-3 border rounded bg-light">
        <Row>
          <Col md={6}>
            <Form.Group controlId="searchQuery">
              <Form.Label>חיפוש</Form.Label>
              <Form.Control
                type="text"
                placeholder="חפש לפי שם, הערות..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="filterLowStock">
              <Form.Label>סינון מלאי נמוך</Form.Label>
              <Form.Select value={filterLowStock} onChange={(e) => setFilterLowStock(e.target.value)}>
                <option value="">הכל</option>
                <option value="true">מלאי נמוך</option>
                <option value="false">מלאי תקין</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      </Form>

      {loading && <Spinner animation="border" role="status"><span className="visually-hidden">טוען מתכלים...</span></Spinner>}
      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && !error && (
        (!consumables || consumables.length === 0) ? (
          <p>אין פריטים מתכלים במערכת. הוסף פריט מתכלה חדש כדי להתחיל, או שנה את מסנני החיפוש.</p>
        ) : (
          <Table striped bordered hover responsive className="text-right">
            <thead>
              <tr>
                <th onClick={() => handleSort('id')} style={{ cursor: 'pointer' }}># {getSortIndicator('id')}</th>
                <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>שם {getSortIndicator('name')}</th>
                <th onClick={() => handleSort('current_stock')} style={{ cursor: 'pointer' }}>מלאי נוכחי {getSortIndicator('current_stock')}</th>
                <th onClick={() => handleSort('default_quantity')} style={{ cursor: 'pointer' }}>כמות ברירת מחדל {getSortIndicator('default_quantity')}</th>
                <th onClick={() => handleSort('min_stock_level')} style={{ cursor: 'pointer' }}>סף מינימלי {getSortIndicator('min_stock_level')}</th>
                <th onClick={() => handleSort('unit')} style={{ cursor: 'pointer' }}>יחידה {getSortIndicator('unit')}</th>
                <th onClick={() => handleSort('last_ordered_date')} style={{ cursor: 'pointer' }}>תאריך הזמנה אחרונה {getSortIndicator('last_ordered_date')}</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {consumables.map(item => (
                <tr key={item.id} className={item.current_stock <= item.min_stock_level && item.min_stock_level > 0 ? 'table-danger' : ''}>
                  <td>{item.id}</td>
                  <td>{item.name}</td>
                  <td>{item.current_stock}</td>
                  <td>{item.default_quantity}</td>
                  <td>{item.min_stock_level}</td>
                  <td>{item.unit || 'N/A'}</td>
                  <td>{item.last_ordered_date || 'N/A'}</td>
                  <td>
                    <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => handleResetToDefault(item)}>
                      <FontAwesomeIcon icon={faRotateLeft} className="me-1" />
                      איפוס לברירת מחדל
                    </Button>
                    <Button variant="warning" size="sm" className="me-2" onClick={() => handleEditClick(item)}>
                      <FontAwesomeIcon icon={faEdit} className="me-1" />
                      ערוך
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => handleDeleteClick(item.id!)} disabled={user?.role?.name === 'WAREHOUSE'} title={user?.role?.name === 'WAREHOUSE' ? 'למחסנאי אין הרשאה למחיקה' : ''}>
                      <FontAwesomeIcon icon={faTrash} className="me-1" />
                      מחק
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )
      )}

      {/* מודאל עבור טופס ההוספה/עריכה - נשאר זהה */}
      <Modal show={showForm} onHide={handleFormCancel} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{consumableToEdit ? 'עריכת פריט מתכלה' : 'הוספת פריט מתכלה'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ConsumableForm
            consumableToEdit={consumableToEdit}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        </Modal.Body>
      </Modal>
     {/* מודאל הודעות חדש */}
            <MessageModal
                show={showMessageModal}
                onHide={() => setShowMessageModal(false)}
                title={messageModalTitle}
                message={messageModalContent}
                variant={messageModalVariant}
            />

            {/* מודאל אישור חדש */}
            <ConfirmationModal
                show={showConfirmModal}
                onHide={() => setShowConfirmModal(false)}
                title={confirmModalTitle}
                message={confirmModalMessage}
                onConfirm={confirmAction || (() => {})}
            />
        </div>
    );
};

export default ConsumablesList;